import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/cn.ts';

/*
 * 按钮。
 *
 * `default` 是**次要**动作（描边），`primary` 才是页面上那一个主动作 ——
 * 一屏里主按钮超过一个，就等于没有主按钮。
 * `danger` 单独一档：它只出现在确认弹窗里那一下（吊销密钥、审批提现），
 * 而不是常驻在列表行上 —— 常亮的红是噪音，只在危险时亮起的红才是信号。
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-[13px] font-medium ' +
    'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ' +
    'focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 ' +
    "[&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 'border border-rule-strong bg-surface text-ink hover:bg-sunk',
        primary: 'bg-primary text-primary-foreground hover:opacity-90',
        danger: 'bg-destructive text-destructive-foreground hover:opacity-90',
        ghost: 'text-ink-muted hover:bg-sunk hover:text-ink',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-3',
        sm: 'h-7 px-2.5 text-xs',
        lg: 'h-9 px-4',
        icon: 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export function Button({
  className, variant, size, asChild = false, ...props
}: ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

export { buttonVariants };
